import React, { useState, useCallback, useMemo } from "react";
import { useForm, useWatch, ArrayField, Controller } from "react-hook-form";
import useTranslations from "../../hooks/useTranslations";
import { DeleteIcon } from "../icons";
import { Button, Checkbox, Input, Slideover, Textarea } from "../wombat-ui";
import QuestionWithOptionsForm, {
  QuestionFormInput,
} from "./QuestionWithOptionsForm";
import QuestionFormTitle from "./QuestionFormTitle";
import InlineItem from "../utils/InlineItem";
import FileUploadButton from "../utils/FileUploadButton";
import { Radio, RadioGroup, StackDivider, VStack } from "@chakra-ui/react";

import useToggleIntercom from "../../hooks/useToggleIntercom";
import useDirectUpload from "../../hooks/useDirectUpload";
import { DocumentPlate } from "../utils/DocumentPlate";
import { uniqueId } from "lodash";

type QuestionFormSlideOverProps = {
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  reset: ReturnType<typeof useForm>["reset"];
  control: ReturnType<typeof useForm>["control"];
  onRemove: () => void;
  name: string;
  defaultValue: Partial<ArrayField<QuestionFormInput, "id">>;
  errors: ReturnType<typeof useForm>["errors"];
  open: boolean;
  onClose: () => void;
};

const QuestionFormSlideOver: React.FC<QuestionFormSlideOverProps> = ({
  register,
  setValue,
  getValues,
  control,
  onRemove,
  name,
  defaultValue,
  errors,
  unregister,
  open = false,
  onClose,
}) => {
  const t = useTranslations();

  // We hide the intercom chat so it doesn't get in the way
  useToggleIntercom(!open);

  const hasTimeTracked = useWatch({
    name: `${name}.timeTracked`,
    control,
    defaultValue: defaultValue.timeTracked,
  });

  const slideoverTitle = useMemo(
    () =>
      ({
        QuestionText: t("templates.answerType.text.label"),
        QuestionSelectorSingle: t("templates.answerType.selectorSingle.label"),
        QuestionSelectorMultiple: t(
          "templates.answerType.selectorMultiple.label"
        ),
        QuestionEvaluation: t("templates.answerType.evaluation.label"),
        QuestionSkill: t("templates.answerType.skillEvaluation.label"),
      }[defaultValue.type]),
    [defaultValue.type]
  );

  const [ documents, setDocuments ] = useState(defaultValue.documents || []);
  const [ upload, { loading: directUploadLoading } ] = useDirectUpload();
  const handleAddDocument = useCallback(async (e) => {
    const { id, signed_id, filename, created_at, ...rest } = await upload(e?.target?.files[0]);
    setDocuments((documents) => [
      ...documents,
      {
        id: uniqueId(),
        blobId: id,
        filename,
        url: `/rails/active_storage/blobs/redirect/${signed_id}/${filename}`,
        createdAt: created_at,
        updatedAt: created_at,
      }
    ]);
  }, []);

  const handleDeleteDocument = useCallback(({ id }) => {
    setDocuments((documents) => documents.filter((document) => document.id !== id))
  }, []);

  return (
    <Slideover
      open={open}
      scale="lg"
      onClose={onClose}
      title={slideoverTitle as string}
    >
      <VStack divider={<StackDivider />} spacing={4} align="stretch" mt={4}>
        <InlineItem title={t("templates.questionTitle")} pl={4} pr={4}>
          <QuestionFormTitle
            register={register}
            unregister={unregister}
            setValue={setValue}
            getValues={getValues}
            defaultValue={defaultValue}
            errors={errors}
            name={name}
          />
        </InlineItem>

        {defaultValue.type !== "QuestionText" && (
          <InlineItem title={t("templates.questionOptions")} pl={4} pr={4}>
            <QuestionWithOptionsForm
              control={control}
              register={register}
              getValues={getValues}
              errors={errors}
              unregister={unregister}
              setValue={setValue}
              defaultValue={defaultValue}
              fixedOptions={
                defaultValue.type === "QuestionEvaluation" ||
                defaultValue.type === "QuestionSkill"
              }
              name={name}
            />
          </InlineItem>
        )}

        {/* <!-- Referent selection --> */}
        <InlineItem title={t("reviews.visibleTo.title")} pl={4} pr={4}>
          <Controller
            control={control}
            name={`${name}.visibility`}
            defaultValue={defaultValue.visibility}
            render={({ onChange, value, name, ref }, state) => (
              <RadioGroup
                onChange={onChange}
                value={value}
                name={name}
              >
                <VStack align="stretch">
                  <Radio id={`${name}.all`} value={"all"} ref={ref}>
                    {t("reviews.visibleTo.all")}
                  </Radio>
                  <Radio id={`${name}.collaborator`} value={"collaborator"} ref={ref}>
                    {t("reviews.visibleTo.collaborator")}
                  </Radio>
                  <Radio id={`${name}.referents`} value={"referents"} ref={ref}>
                    {t("reviews.visibleTo.referents")}
                  </Radio>
                </VStack>
              </RadioGroup>
            )}
          />
        </InlineItem>

        <InlineItem title={t("templates.questionDescription")} pl={4} pr={4}>
          <Textarea
            id={`${name}.description`}
            srOnlyLabel
            defaultValue={defaultValue.description}
            name={`${name}.description`}
            placeholder={t("templates.questionDescription") as string}
            rows={4}
            ref={register()}
            label={t("templates.questionDescription") as string}
          />
        </InlineItem>
        <InlineItem title={t("templates.questionRequired")} pl={4} pr={4}>
        <Checkbox
            label={t("templates.questionRequired") as string}
            srOnlyLabel
            id={`${name}.required`}
            name={`${name}.required`}
            defaultChecked={defaultValue.required}
            ref={register()}
          />
        </InlineItem>
        <InlineItem title={t("reviews.reportsIncludingQuestion")} pl={4} pr={4}>
          <Checkbox
            label={t("reports.header.FirstReport") as string}
            id={`${name}.reportTypes_onboarding`}
            name={`${name}.reportTypes`}
            value={"FirstReport"}
            help={t("reviews.firstReportDescription") as string}
            defaultChecked={defaultValue.reportTypes.includes("FirstReport")}
            ref={register()}
          />
          <Checkbox
            label={t("reports.header.IntermediateReport") as string}
            id={`${name}.reportTypes_intermediate`}
            name={`${name}.reportTypes`}
            value={"IntermediateReport"}
            help={t("reviews.intermediateReportDescription") as string}
            defaultChecked={defaultValue.reportTypes.includes(
              "IntermediateReport"
            )}
            ref={register()}
          />
          <Checkbox
            label={t("reports.header.FinalReport") as string}
            id={`${name}.reportTypes_final`}
            name={`${name}.reportTypes`}
            help={t("reviews.lastReportDescription") as string}
            value={"FinalReport"}
            defaultChecked={defaultValue.reportTypes.includes("FinalReport")}
            ref={register()}
          />
        </InlineItem>

        {/* <!-- Time tracking --> */}
        <InlineItem title={t("reviews.timeTracked")} pl={4} pr={4}>
          <Checkbox
            label={t("reviews.timeTrackedEnable") as string}
            help={t("reviews.timeTrackedDescription") as string}
            id={`${name}.timeTracked`}
            name={`${name}.timeTracked`}
            ref={register()}
            defaultChecked={defaultValue.timeTracked}
          />
        </InlineItem>

        {/* <!-- Expected time spent --> */}
        {hasTimeTracked && (
          <InlineItem title={t("reviews.expectedTimeValue")} pl={4} pr={4}>
            <Input
              size="xsmall"
              ref={register({ valueAsNumber: true })}
              label={t("reviews.expectedTimeValue") as string}
              srOnlyLabel
              help={t("forms.unit.timeInHour") as string}
              id={`${name}.expectedTimeValue`}
              name={`${name}.expectedTimeValue`}
              type="number"
              defaultValue={defaultValue.expectedTimeValue}
            />
          </InlineItem>
        )}

        <InlineItem title={t("reviews.attachments")} pl={4} pr={4}>
          {
            documents.map((document, index) => (
              <React.Fragment key={document.id}>
                <DocumentPlate
                  document={document}
                  allowDelete
                  onDelete={handleDeleteDocument}
                />
                {
                  ["id", "blobId", "filename", "url", "createdAt", "updatedAt"].map((documentAttribute) => (
                    <input
                      type="hidden"
                      name={`${name}.documents[${index}].${documentAttribute}`}
                      value={document[documentAttribute]}
                      ref={register()}
                    />
                  ))
                }

              </React.Fragment>
            ))
          }
          <FileUploadButton
            onChange={handleAddDocument}
            name={`${name}.attachment`}
            isLoading={directUploadLoading}
          >
            {t("reviews.addAttachment")}
          </FileUploadButton>
        </InlineItem>

        <InlineItem title={t("reviews.questionActions")} pl={4} pr={4}>
          <Button
            variant="subtle"
            onClick={onRemove}
            trailingIcon={DeleteIcon}
            id={`${name}.deleteQuestion`}
          >
            {t("reviews.deleteQuestion")}
          </Button>
        </InlineItem>
      </VStack>
    </Slideover>
  );
};

export default React.memo(QuestionFormSlideOver);
